import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Select from '@mulesoft/anypoint-components/lib/Select';
import styles from './AuthenticationFields.css';

/**
 * AWS Region options for IAM authentication
 */
const AWS_REGION_OPTIONS = [
  { label: 'US East (N. Virginia) - us-east-1', value: 'us-east-1' },
  { label: 'US East (Ohio) - us-east-2', value: 'us-east-2' },
  { label: 'US West (N. California) - us-west-1', value: 'us-west-1' },
  { label: 'US West (Oregon) - us-west-2', value: 'us-west-2' },
  { label: 'Europe (Ireland) - eu-west-1', value: 'eu-west-1' },
  { label: 'Europe (Frankfurt) - eu-central-1', value: 'eu-central-1' },
  {
    label: 'Asia Pacific (Singapore) - ap-southeast-1',
    value: 'ap-southeast-1'
  },
  { label: 'Asia Pacific (Sydney) - ap-southeast-2', value: 'ap-southeast-2' },
  { label: 'Asia Pacific (Tokyo) - ap-northeast-1', value: 'ap-northeast-1' }
];

/**
 * AuthenticationFields Component
 *
 * Renders authentication-specific fields based on the selected authentication method:
 * - IAM Access Key: Access Key ID, Secret Access Key, AWS Region
 * - OAuth: Client ID, Client Secret, Authorization Server URL, Token Endpoint URL, Redirect URI, Scope
 */
const AuthenticationFields = ({ authMethod, formData, onChange }) => {
  // Memoize region options
  const awsRegionOptions = useMemo(() => AWS_REGION_OPTIONS, []);

  // IAM Field handlers
  const handleAccessKeyIdChange = useCallback(
    (e) => onChange('accessKeyId', e.value),
    [onChange]
  );

  const handleSecretAccessKeyChange = useCallback(
    (e) => onChange('secretAccessKey', e.value),
    [onChange]
  );

  const handleAwsRegionChange = useCallback(
    (event) => onChange('awsRegion', event.itemsSelected?.value || ''),
    [onChange]
  );

  // OAuth Field handlers
  const handleClientIdChange = useCallback(
    (e) => onChange('clientId', e.value),
    [onChange]
  );

  const handleClientSecretChange = useCallback(
    (e) => onChange('clientSecret', e.value),
    [onChange]
  );

  const handleAuthorizationServerURLChange = useCallback(
    (e) => onChange('authorizationServerURL', e.value),
    [onChange]
  );

  const handleTokenEndpointURLChange = useCallback(
    (e) => onChange('tokenEndpointURL', e.value),
    [onChange]
  );

  const handleRedirectURIChange = useCallback(
    (e) => onChange('redirectURI', e.value),
    [onChange]
  );

  const handleScopeChange = useCallback(
    (e) => onChange('scope', e.value),
    [onChange]
  );

  const renderIAMFields = () => (
    <div className={styles['auth-fields']} data-test-id="iam-auth-fields">
      <div className={styles.field}>
        <label htmlFor="access-key-id" className={styles.label}>
          Access Key ID <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="access-key-id"
            name="accessKeyId"
            value={formData.accessKeyId}
            onChange={handleAccessKeyIdChange}
            required
            placeholder="Enter AWS Access Key ID"
            testId="access-key-id-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="secret-access-key" className={styles.label}>
          Secret Access Key <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="secret-access-key"
            name="secretAccessKey"
            type="password"
            value={formData.secretAccessKey}
            onChange={handleSecretAccessKeyChange}
            required
            placeholder="Enter AWS Secret Access Key"
            testId="secret-access-key-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="aws-region" className={styles.label}>
          AWS Region <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="aws-region"
            name="aws-region"
            testId="aws-region-select"
            value={formData.awsRegion}
            placeholder="Select AWS region"
            options={awsRegionOptions}
            onChange={handleAwsRegionChange}
          />
        </div>
      </div>
    </div>
  );

  const renderOAuthFields = () => (
    <div className={styles['auth-fields']} data-test-id="oauth-auth-fields">
      <div className={styles.field}>
        <label htmlFor="client-id" className={styles.label}>
          Client ID <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="client-id"
            name="clientId"
            value={formData.clientId}
            onChange={handleClientIdChange}
            required
            placeholder="Enter OAuth Client ID"
            testId="client-id-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="client-secret" className={styles.label}>
          Client Secret <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="client-secret"
            name="clientSecret"
            type="password"
            value={formData.clientSecret}
            onChange={handleClientSecretChange}
            required
            placeholder="Enter OAuth Client Secret"
            testId="client-secret-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="authorization-server-url" className={styles.label}>
          Authorization Server URL <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="authorization-server-url"
            name="authorizationServerURL"
            value={formData.authorizationServerURL}
            onChange={handleAuthorizationServerURLChange}
            required
            placeholder="https://auth.example.com"
            testId="authorization-server-url-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="token-endpoint-url" className={styles.label}>
          Token Endpoint URL <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="token-endpoint-url"
            name="tokenEndpointURL"
            value={formData.tokenEndpointURL}
            onChange={handleTokenEndpointURLChange}
            required
            placeholder="https://token.example.com"
            testId="token-endpoint-url-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="redirect-uri" className={styles.label}>
          Redirect URI <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="redirect-uri"
            name="redirectURI"
            value={formData.redirectURI}
            onChange={handleRedirectURIChange}
            required
            placeholder="https://redirect.example.com"
            testId="redirect-uri-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="scope" className={styles.label}>
          Scope
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="scope"
            name="scope"
            value={formData.scope}
            onChange={handleScopeChange}
            placeholder="openid profile email"
            testId="scope-input"
          />
        </div>
      </div>
    </div>
  );

  if (authMethod === 'iam-access-key') {
    return renderIAMFields();
  }

  if (authMethod === 'oauth') {
    return renderOAuthFields();
  }

  return null;
};

AuthenticationFields.propTypes = {
  /** Selected authentication method */
  authMethod: PropTypes.string.isRequired,
  /** Form data object containing all field values */
  formData: PropTypes.shape({
    accessKeyId: PropTypes.string,
    secretAccessKey: PropTypes.string,
    awsRegion: PropTypes.string,
    clientId: PropTypes.string,
    clientSecret: PropTypes.string,
    authorizationServerURL: PropTypes.string,
    tokenEndpointURL: PropTypes.string,
    redirectURI: PropTypes.string,
    scope: PropTypes.string
  }).isRequired,
  /** Change handler for form fields */
  onChange: PropTypes.func.isRequired
};

export default AuthenticationFields;
